import {
  User as CoreUser,
} from '@policyfly/protobuf'
import { User as PatrickUser, UserDetail } from '@policyfly/protobuf/patrick'

import type { User as ActivityUser } from '@policyfly/protobuf/pfactivity'
import type { UnifiedMembershipUser } from 'types/user'

/**
 * Converts Protobuf {@link CoreUser User} from grpc core definitions
 * to equivalent Protobuf {@link UserDetail} from patrick definitions.
 */
export function coreUserToUserDetail (user: CoreUser): UserDetail {
  return UserDetail.create({
    legacyId: user.id,
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    bingoId: user.ulid,
  })
}

/**
 * Converts Protobuf {@link CoreUser User} from grpc core definitions
 * to equivalent Protobuf {@link PatrickUser User} from patrick definitions.
 */
export function coreUserToPatrickUser (user: CoreUser): PatrickUser {
  return PatrickUser.create({
    userDetail: coreUserToUserDetail(user),
  })
}

/**
 * Converts Protobuf {@link UserDetail} from patrick definitions
 * to equivalent Protobuf {@link CoreUser User} from grpc core definitions.
 */
export function userDetailToCoreUser (user?: UserDetail): CoreUser {
  return CoreUser.create({
    id: user?.legacyId,
    email: user?.email,
    firstName: user?.firstName,
    lastName: user?.lastName,
    ulid: user?.bingoId,
  })
}

/**
 * Converts Protobuf {@link PatrickUser User} from patrick definitions
 * to equivalent Protobuf {@link CoreUser User} from grpc core definitions.
 */
export function patrickUserToCoreUser (user: PatrickUser): CoreUser {
  return userDetailToCoreUser(user.userDetail)
}

/**
 * Converts Django {@link UnifiedMembershipUser}
 * to equivalent Protobuf {@link CoreUser User} from grpc core definitions.
 */
export function userJsonToCoreUser (user?: Partial<UnifiedMembershipUser> | null): CoreUser {
  return CoreUser.create({
    id: user?.id || 0,
    email: user?.email || '',
    firstName: user?.first_name || '',
    lastName: user?.last_name || '',
    ulid: user?.bingoId || undefined,
  })
}

/**
 * Converts Django {@link UnifiedMembershipUser}
 * to equivalent Protobuf {@link UserDetail} from patrick definitions.
 */
export function userJsonToUserDetail (user?: Partial<UnifiedMembershipUser> | null): UserDetail {
  return UserDetail.create({
    legacyId: user?.id || undefined,
    email: user?.email || '',
    firstName: user?.first_name || '',
    lastName: user?.last_name || '',
    avatar: user?.avatar_url || undefined,
    bingoId: user?.bingoId || undefined,
  })
}

/**
 * Converts Django {@link UnifiedMembershipUser}
 * to equivalent Protobuf {@link PatrickUser User} from patrick definitions.
 */
export function userJsonToPatrickUser (user?: Partial<UnifiedMembershipUser> | null): PatrickUser {
  return PatrickUser.create({
    userDetail: userJsonToUserDetail(user),
    activated: user?.active_at || undefined,
    isActive: user?.is_active || false,
  })
}

/**
 * Converts Protobuf {@link CoreUser User} from grpc core definitions
 * to equivalent Django {@link UnifiedMembershipUser}.
 */
export function coreUserToUserJson (user?: CoreUser): UnifiedMembershipUser | null {
  if (!user) return null
  return {
    id: user.id || 0,
    email: user.email || '',
    first_name: user.firstName || '',
    last_name: user.lastName || '',
    avatar_url: '',
    is_active: false,
    active_at: '',
    isClerk: false,
    bingoId: user.ulid || undefined,
  } as UnifiedMembershipUser
}

/**
 * Converts Protobuf {@link PatrickUser User} from patrick definitions
 * to equivalent Django mentioned_user for comments.
 */
export function patrickUserToMention (user: PatrickUser): Pick<UnifiedMembershipUser, 'email' | 'first_name' | 'last_name'> {
  return {
    email: user.userDetail?.email ?? '',
    first_name: user.userDetail?.firstName ?? '',
    last_name: user.userDetail?.lastName ?? '',
  }
}

/**
 * Converts Protobuf {@link CoreUser User} from grpc core definitions
 * to equivalent Protobuf {@link ActivityUser User} from pfactivity definitions.
 */
export function coreUserToActivityUser (user: CoreUser): ActivityUser {
  return {
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    id: user.ulid,
    legacyId: user.id,
  }
}

/**
 * Converts Protobuf {@link PatrickUser User} from patrick definitions
 * to equivalent Protobuf {@link ActivityUser User} from pfactivity definitions.
 */
export function patrickUserToActivityUser (user: PatrickUser): ActivityUser {
  return {
    email: user.userDetail?.email ?? '',
    firstName: user.userDetail?.firstName ?? '',
    lastName: user.userDetail?.lastName ?? '',
    id: user.userDetail?.bingoId ?? '',
    legacyId: user.userDetail?.legacyId || 0,
  }
}
